'use client';

import { useState, useEffect } from 'react';
import { ArrowLeft, X } from 'lucide-react';
import { getSourceTool, hasSourceTool, getToolDisplayName, TOOL_PATHS, type ToolKey } from '@/lib/tools/tool-links';

/**
 * Breadcrumb component that shows "Back to X" when arriving from another tool.
 * Reads source from URL ?source= parameter.
 */
export default function SourceBreadcrumb() {
  const [source, setSource] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Only run on client
    if (hasSourceTool()) {
      const sourceTool = getSourceTool();
      setSource(sourceTool);

      // Check if user previously dismissed this breadcrumb
      const dismissedKey = `chain-breadcrumb-dismissed-${sourceTool}`;
      if (sessionStorage.getItem(dismissedKey) === 'true') {
        setDismissed(true);
      }
    }
  }, []);

  const handleDismiss = () => {
    if (source) {
      sessionStorage.setItem(`chain-breadcrumb-dismissed-${source}`, 'true');
    }
    setDismissed(true);
  };

  // Don't render if no source or dismissed
  if (!source || dismissed) {
    return null;
  }

  // Get the path for the source tool
  const sourcePath = source in TOOL_PATHS
    ? TOOL_PATHS[source as ToolKey]
    : `/tools/${source}`;

  const displayName = getToolDisplayName(source as ToolKey);

  return (
    <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center justify-between">
      <a
        href={sourcePath}
        className="flex items-center gap-2 text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 font-medium text-sm transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back to {displayName}</span>
      </a>
      <button
        onClick={handleDismiss}
        className="p-1 text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-200 rounded transition-colors"
        title="Dismiss"
        aria-label="Dismiss breadcrumb"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
